import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Spade, Crown, Clock, Check } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { Flag } from "../components/Flag";
import { useAuth } from "../context/AuthContext";
import { Page, Loader, Empty, GoldButton, GhostButton, Card } from "../components/UI";
import { useEventStream } from "../lib/sse";

export default function ChallengeTablePage() {
  const { id } = useParams();
  const { player } = useAuth();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  const [bet, setBet] = useState("");
  const [actions, setActions] = useState([]);

  const load = useCallback(() => {
    api.get(`/challenge/${id}`).then(({ data }) => setD(data)).catch(() => setMissing(true));
    api.get(`/challenge/${id}/actions`).then(({ data }) => setActions(data.actions || [])).catch(() => setActions([]));
  }, [id]);

  useEffect(() => { load(); const t = setInterval(load, 2500); return () => clearInterval(t); }, [load]);
  useEventStream(`/live/stream`, (ev) => { if (ev?.type?.startsWith?.("table.")) load(); });

  const act = async (fn) => { try { await fn(); load(); } catch (e) { toast.error(e?.response?.data?.detail?.message || apiErr(e)); } };

  if (missing) return <Page><Empty label="Table not found." /></Page>;
  if (!d) return <Loader label="Loading table…" />;
  const t = d.table;
  const myStatus = d.me?.status;
  const canBet = t.status === "LIVE" && myStatus === "ACTIVE" && !d.current_bet;
  const myPending = d.current_bet?.my_pending;

  return (
    <Page>
      <Link to="/challenge" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#E5B842] mb-6" style={{ transition: "color 0.15s ease" }}>
        <ArrowLeft className="w-4 h-4" /> All tables
      </Link>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-fuchsia-300 font-bold">{t.tier.replace(/_/g, " ")} · {t.public_id}</div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tight text-slate-50"><Spade className="w-7 h-7 inline -mt-2 text-fuchsia-300" /> Challenge Table</h1>
          <div className="text-sm text-slate-400 mt-1">Buy-in {t.buy_in} VP · Pot <span className="text-[#E5B842] font-bold">{t.pot} VP</span> · {t.status}</div>
        </div>
      </div>

      {t.showcase && t.showcase_note && (
        <div data-testid="challenge-showcase-note" className="rounded-xl bg-[#1a1407]/60 border border-[#E5B842]/40 p-4 mb-6 text-sm text-[#E5B842] font-mono">ⓘ {t.showcase_note}</div>
      )}

      {t.winners && t.winners.length > 0 && (
        <div data-testid="challenge-winner" className="rounded-2xl foil-border bg-gradient-to-b from-[#1a1407] to-[#070707] p-6 mb-6 flex items-center gap-4">
          <Crown className="w-10 h-10 text-[#E5B842]" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold">{t.foldout ? "Won by fold-out" : t.split ? "Split pot" : "Winner"} — {t.pot} VP</div>
            <div className="font-heading text-2xl font-black uppercase text-slate-50">{t.winners.map((w) => w?.display_name).join(" · ")}</div>
          </div>
        </div>
      )}

      {/* Current bet + my action */}
      {d.current_bet && (
        <Card className="p-5 mb-6 foil-border" data-testid="challenge-current-bet">
          <div className="flex items-center justify-between">
            <div className="text-slate-200"><span className="font-bold text-fuchsia-300">{d.current_bet.bettor?.display_name}</span> bet <span className="font-heading font-black text-[#E5B842]">{d.current_bet.amount} VP</span></div>
            <Countdown deadline={d.current_bet.deadline} />
          </div>
          {myPending && (
            <div className="flex gap-2 mt-4">
              <GoldButton data-testid="btn-call" onClick={() => act(() => api.post(`/challenge/${id}/respond`, { action: "CALL" }))}>CALL {d.current_bet.amount}</GoldButton>
              <GhostButton data-testid="btn-fold" onClick={() => act(() => api.post(`/challenge/${id}/respond`, { action: "FOLD" }))}>FOLD</GhostButton>
            </div>
          )}
        </Card>
      )}

      {/* My bet control */}
      {canBet && (
        <Card className="p-4 mb-6 flex items-center gap-3" data-testid="challenge-bet-control">
          <input data-testid="bet-amount" type="number" value={bet} onChange={(e) => setBet(e.target.value)}
            placeholder={`${t.buy_in * 0.5}–${t.buy_in * 5} VP`} className="flex-1 bg-[#000000] border border-[#2b2512] rounded-lg px-3 py-2 text-slate-100 font-mono" />
          <GoldButton data-testid="btn-bet" disabled={!bet} onClick={() => act(async () => { await api.post(`/challenge/${id}/bet`, { amount: parseFloat(bet) }); setBet(""); })}>Open BET</GoldButton>
        </Card>
      )}

      {t.status === "FORMING" && d.me?.seated && !d.me?.submitted && (
        <CouponPanel id={id} onDone={load} />
      )}
      {t.status === "FORMING" && d.me?.seated && d.me?.submitted && (
        <Card className="p-4 mb-6 text-emerald-400 flex items-center gap-2" data-testid="coupon-locked"><Check className="w-5 h-5" /> Your secret coupon is locked. Waiting for the table to fill…</Card>
      )}

      {/* Seats */}
      <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3">Players ({d.seats.length})</h2>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="challenge-seats">
        {d.seats.map((s, i) => (
          <div key={i} data-testid={`seat-${i}`} className={`rounded-xl bg-[#0f0f0f] border p-4 ${s.status === "FOLDED" ? "border-red-500/30 opacity-60" : "border-[#2b2512]"}`}>
            <div className="flex items-center justify-between">
              <span className="font-heading font-bold uppercase text-slate-100 text-sm">{s.player?.country && <Flag country={s.player.country} className="inline-block mr-1 align-middle" />}{s.player?.display_name}{s.is_bot ? " 🤖" : ""}</span>
              <span className={`font-mono text-[10px] uppercase tracking-wider ${s.status === "FOLDED" ? "text-red-400" : "text-emerald-400"}`}>{s.status}</span>
            </div>
            <div className="text-[11px] font-mono text-slate-500 mt-1">stake {s.stake} VP · {s.coupon_submitted ? "coupon set" : "no coupon"}</div>
            {s.coupon ? (
              <div className="mt-2 space-y-1" data-testid={`seat-coupon-${i}`}>
                {(s.coupon.selections || []).map((sel, j) => (
                  <div key={j} data-testid={`seat-${i}-sel-${j}`} className="flex items-center justify-between gap-2 rounded bg-[#000000] border border-[#2b2512] px-2 py-1">
                    <div className="min-w-0">
                      <div className="text-xs text-slate-200 truncate">{sel.event_label}</div>
                      <div className="text-[10px] font-mono text-slate-500 truncate">{sel.market_name} · {sel.label} <span className="text-slate-400">@{sel.odds_str}</span></div>
                    </div>
                    <SelResult result={sel.result} />
                  </div>
                ))}
                <div className="text-[10px] font-mono text-slate-500 pt-0.5">{s.coupon.num_selections} selections · {s.coupon.total_odds_str} total odds</div>
              </div>
            ) : s.coupon_submitted ? (
              <div className="mt-2 text-xs text-slate-600 italic" data-testid={`seat-hidden-${i}`}>🔒 hidden until reveal</div>
            ) : null}
          </div>
        ))}
      </div>

      {t.status === "RESOLVED" && d.ranking?.length > 0 && (
        <div className="mt-8" data-testid="challenge-resolution">
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3">Resolution</h2>
          <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-[9px] uppercase tracking-wider text-slate-500 font-mono"><th className="text-left px-4 py-2">#</th><th className="text-left px-2 py-2">Player</th><th className="text-right px-2 py-2">Lost</th><th className="text-right px-4 py-2">Odds</th></tr></thead>
              <tbody>
                {d.ranking.map((r) => (
                  <tr key={r.player_id} className={`border-t border-[#2b2512]/60 ${r.position === 1 ? "text-[#E5B842]" : "text-slate-300"}`}>
                    <td className="px-4 py-2 font-heading font-black">{r.position === 1 ? <Crown className="w-3.5 h-3.5 inline" /> : r.position}</td>
                    <td className="px-2 py-2">{r.player?.display_name}{r.foldout ? " (bluff win)" : r.folded ? " (folded)" : ""}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.folded ? "—" : r.lost}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-mono">{r.folded ? "—" : (r.odds || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(t.status === "LIVE" || t.status === "RESOLVED") && actions.length > 0 && (
        <ActionTimeline actions={actions} betTimer={15} />
      )}
    </Page>
  );
}

function SelResult({ result }) {
  const map = {
    WON: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    LOST: "bg-red-500/15 text-red-400 border-red-500/30",
    VOID: "bg-slate-500/15 text-slate-400 border-slate-500/30",
    PENDING: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  };
  return <span data-testid={`sel-result-${result}`} className={`shrink-0 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[result] || map.PENDING}`}>{result || "PENDING"}</span>;
}

function ActionTimeline({ actions }) {
  const fmt = (iso) => new Date(iso).toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const line = (a) => {
    const who = a.player?.display_name || "—";
    if (a.type === "JOIN") return <><span className="text-slate-200 font-semibold">{who}</span> joined <span className="font-mono text-slate-500">({a.amount} VP)</span></>;
    if (a.type === "BETTING_LOCK_SET") return <>Live phase started · betting locks at <span className="font-mono text-amber-300">{fmt(a.lock_at)}</span> · pot {a.pot} VP</>;
    if (a.type === "BET") return <><span className="text-fuchsia-300 font-semibold">{who}</span> opened a <span className="font-heading font-black text-[#E5B842]">BET {a.amount} VP</span> · 15s to respond (deadline <span className="font-mono text-amber-300">{fmt(a.deadline)}</span>)</>;
    if (a.action === "CALL") return <><span className="text-slate-200 font-semibold">{who}</span> <span className="text-emerald-400 font-bold">CALLED</span></>;
    if (a.action === "FOLD") return <><span className="text-slate-200 font-semibold">{who}</span> <span className="text-red-400 font-bold">FOLDED</span>{a.auto ? <span className="font-mono text-[10px] text-red-300 ml-1">(auto · 15s timeout)</span> : ""}{a.fold_order ? <span className="font-mono text-slate-500 ml-1">#{a.fold_order}</span> : ""}</>;
    if (a.type === "RESOLVED") return <><span className="text-[#E5B842] font-bold">SETTLED</span> · winner {(a.winners || []).map((w) => w?.display_name).join(", ")}{a.foldout ? " (fold-out)" : a.split ? " (split)" : ""} · pot {a.pot} VP</>;
    return a.type;
  };
  const dot = (a) => a.type === "BET" ? "bg-[#E5B842]" : a.action === "FOLD" ? "bg-red-400" : a.action === "CALL" ? "bg-emerald-400" : a.type === "RESOLVED" ? "bg-[#E5B842]" : "bg-slate-500";
  return (
    <div className="mt-8" data-testid="challenge-action-timeline">
      <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-1">Action Timeline</h2>
      <p className="text-[11px] font-mono text-slate-500 mb-3">Server-authoritative log — every BET starts a 15-second window; no response = automatic fold.</p>
      <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4 space-y-2.5">
        {actions.map((a, i) => (
          <div key={i} data-testid={`action-${i}`} className="flex items-start gap-3 text-sm">
            <span className="font-mono text-[10px] text-slate-500 tabular-nums pt-0.5 shrink-0 w-16">{fmt(a.at)}</span>
            <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${dot(a)}`} />
            <span className="text-slate-300 leading-snug">{line(a)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Countdown({ deadline }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.round((new Date(deadline) - new Date()) / 1000)));
    tick(); const i = setInterval(tick, 500); return () => clearInterval(i);
  }, [deadline]);
  return <span data-testid="bet-countdown" className={`font-mono text-lg font-black ${left <= 5 ? "text-red-400" : "text-amber-300"}`}><Clock className="w-4 h-4 inline" /> {left}s</span>;
}

function CouponPanel({ id, onDone }) {
  const [events, setEvents] = useState(null);
  const [picks, setPicks] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/challenge/${id}/eligible-events`).then(({ data }) => setEvents(data.events)).catch(() => setEvents([])); }, [id]);
  const total = useMemo(() => picks.reduce((a, p) => a * parseFloat(p.odds), 1), [picks]);
  const toggle = (ev, m, s) => setPicks((prev) => {
    if (prev.find((p) => p.selection_id === s.selection_id)) return prev.filter((p) => p.selection_id !== s.selection_id);
    if (prev.length >= 6) { toast.error("Max 6 selections"); return prev; }
    return [...prev, { event_id: ev.id, market_id: m.market_id, selection_id: s.selection_id, odds: s.odds }];
  });
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/challenge/${id}/coupon`, { selections: picks.map((p) => ({ event_id: p.event_id, market_id: p.market_id, selection_id: p.selection_id })) }); toast.success("Secret coupon locked"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-5 mb-6 foil-border" data-testid="challenge-coupon-panel">
      <h3 className="font-heading text-lg font-bold uppercase text-slate-50 mb-3">Build your secret coupon (1–6 selections)</h3>
      {!events ? <Loader label="Loading events…" /> : events.length === 0 ? <Empty label="No eligible events right now." /> : (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3 max-h-72 overflow-auto pr-1" data-testid="challenge-events">
            {events.map((ev) => (
              <div key={ev.id} className="rounded-lg bg-[#000000] border border-[#2b2512] p-3">
                <div className="font-heading font-bold uppercase text-slate-100 text-sm">{ev.home_name} vs {ev.away_name}</div>
                {ev.markets.map((m) => (
                  <div key={m.market_id} className="mt-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">{m.name}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {m.selections.map((s) => {
                        const sel = picks.some((p) => p.selection_id === s.selection_id);
                        return <button key={s.selection_id} data-testid={`ch-sel-${s.selection_id}`} onClick={() => toggle(ev, m, s)}
                          className={`px-2.5 py-1 rounded text-xs font-semibold border ${sel ? "bg-[#E5B842] text-[#000000] border-[#E5B842]" : "bg-[#0f0f0f] text-slate-200 border-[#2b2512] hover:border-[#E5B842]/40"}`} style={{ transition: "border-color 0.15s ease" }}>{s.label} <span className="font-mono">{s.odds}</span></button>;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div>
            <div className="rounded-lg bg-[#000000] border border-[#2b2512] p-4">
              <div className="flex justify-between text-sm"><span className="text-slate-400 font-mono text-[10px] uppercase">Selections</span><span className="text-slate-100">{picks.length}/6</span></div>
              <div className="flex justify-between items-center mt-2"><span className="text-slate-400 font-mono text-[10px] uppercase">Total odds</span><span className="font-heading text-2xl font-black text-[#E5B842] tabular-nums">{total.toFixed(2)}</span></div>
              <GoldButton className="w-full mt-4" data-testid="ch-coupon-submit" disabled={picks.length === 0 || busy} onClick={submit}>{busy ? "Locking…" : "Lock coupon"}</GoldButton>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
